/**
 * styledText.tsx — render legacy chat-message styling SAFELY.
 *
 *   [style=rotate]hello[/style]   → rainbow letters
 *   [style=invert]hello[/style]   → light-on-dark inverted swatch
 *   [style=bbc]…[/style]          → render the BBCode subset inside
 *   [style=body]hello[/style]     → bold + spaced
 *   [b], [i], [u], [color=#RGB], [url=...]link[/url]   inside BBCode
 *
 * Everything else passes through as plain text. No dangerouslySetInnerHTML.
 */
import type { ReactNode } from 'react';
import { autolink } from './autolink.js';
import { hasLegacyHtml, renderLegacyHtml } from './legacyHtml.js';

const STYLE_TAG_RE = /\[style=(rotate|invert|bbc|body)\](.*?)\[\/style\]/gi;

const RAINBOW = ['#FF6618', '#F7E138', '#13CC23', '#009eef', '#7c83d4', '#8f0cc7', '#E818FF'];

function renderRotate(s: string): ReactNode {
    return Array.from(s).map((ch, i) => (
        <span key={i} style={{ color: RAINBOW[i % RAINBOW.length] }}>{ch}</span>
    ));
}

function renderInvert(s: string): ReactNode {
    return <span className="msg-invert">{s}</span>;
}

function renderBody(s: string): ReactNode {
    return <span className="msg-bold-wide">{s}</span>;
}

/** Recursive BBCode parser for a small whitelist of tags. */
function renderBBC(s: string, keyBase = ''): ReactNode[] {
    const out: ReactNode[] = [];
    let i = 0;
    let key = 0;
    while (i < s.length) {
        const m = /\[(\/)?(b|i|u|color(?:=[^\]]+)?|url(?:=[^\]]+)?)\]/i.exec(s.slice(i));
        if (!m) { out.push(s.slice(i)); break; }
        if (m.index > 0) out.push(s.slice(i, i + m.index));
        const tag = m[2]!.toLowerCase();
        const isClose = m[1] === '/';
        const tagBase = tag.split('=')[0]!;
        if (isClose) { i += m.index + m[0].length; continue; }
        // find matching close
        const after = i + m.index + m[0].length;
        const closeRe = new RegExp(`\\[/${tagBase}\\]`, 'i');
        const closeM = closeRe.exec(s.slice(after));
        if (!closeM) { out.push(s.slice(i + m.index)); break; }
        const inner = s.slice(after, after + closeM.index);
        const k = `${keyBase}-${key++}`;
        if (tagBase === 'b') out.push(<b key={k}>{renderBBC(inner, k)}</b>);
        else if (tagBase === 'i') out.push(<i key={k}>{renderBBC(inner, k)}</i>);
        else if (tagBase === 'u') out.push(<u key={k}>{renderBBC(inner, k)}</u>);
        else if (tagBase === 'color') {
            const c = (tag.match(/^color=(.+)$/i)?.[1] ?? '').trim();
            const safe = /^#[0-9A-Fa-f]{3,6}$|^[a-zA-Z]+$/.test(c) ? c : 'inherit';
            out.push(<span key={k} style={{ color: safe }}>{renderBBC(inner, k)}</span>);
        }
        else if (tagBase === 'url') {
            const u = (tag.match(/^url=(.+)$/i)?.[1] ?? inner).trim();
            const safe = /^https?:\/\/[^"<>\s]{1,512}$/.test(u) ? u : '#';
            out.push(<a key={k} href={safe} target="_blank" rel="noopener nofollow">{renderBBC(inner, k)}</a>);
        }
        i = after + closeM.index + closeM[0].length;
    }
    return out;
}

/** Render a chat body with optional [style=…] wrappers + URL/@user autolinks
 *  + safe legacy-HTML subset (<b>/<i>/<u>/<s>/<font color size>). Safe by
 *  construction: never dangerouslySetInnerHTML. */
export function renderStyledBody(body: string): ReactNode {
    if (!body) return body;
    if (!body.includes('[style=')) {
        // No [style=…] wrappers. If the body contains legacy HTML, render
        // that (and skip autolink — keeps HTML tags from being treated as
        // text). Otherwise pass through autolink for URLs + @mentions.
        return hasLegacyHtml(body) ? renderLegacyHtml(body) : autolink(body);
    }
    STYLE_TAG_RE.lastIndex = 0;
    const pieces: ReactNode[] = [];
    let last = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = STYLE_TAG_RE.exec(body))) {
        if (m.index > last) pieces.push(...autolink(body.slice(last, m.index)));
        const style = m[1]!.toLowerCase();
        const inner = m[2]!;
        const k = `s-${key++}`;
        if (style === 'rotate')      pieces.push(<span key={k}>{renderRotate(inner)}</span>);
        else if (style === 'invert') pieces.push(<span key={k}>{renderInvert(inner)}</span>);
        else if (style === 'body')   pieces.push(<span key={k}>{renderBody(inner)}</span>);
        else if (style === 'bbc')    pieces.push(<span key={k}>{renderBBC(inner, k)}</span>);
        else                          pieces.push(inner);
        last = m.index + m[0].length;
    }
    if (last < body.length) pieces.push(...autolink(body.slice(last)));
    return pieces;
}
